// src/components/dashboard/DashboardBeersTab.jsx
// ============================================================
// BEERS ON TAP TAB - Shows beers currently on tap by location
// ============================================================
// Groups beers by location/sublocation with expandable sections.
// Sorts beers within each group by configured tap order.
// Props: locations, beersByLocation, expandedLocations, toggleLocationExpansion,
//        account, calculateAverageRating, getTotalRatings, renderStars,
//        onOpenRatingModal, onViewKeg
// ============================================================

import React from 'react';
import { ChevronDown, ChevronRight, MapPin /*, Star */ } from 'lucide-react';
import { BeerCard } from '../shared/BeerCard';
import { sortBeersByTapOrder } from '../../utils/filterUtils';

/**
 * LocationHeader - Expandable header for a location group
 */
const LocationHeader = ({ 
  location, 
  beerCount, 
  isExpanded, 
  onToggle, 
  onViewTapList,
  variant = 'amber' // 'amber' for on-tap, 'blue' for on-deck
}) => {
  const bgClass = variant === 'amber' 
    ? 'bg-amber-600 hover:bg-amber-700' 
    : 'bg-blue-600 hover:bg-blue-700';
  const textClass = variant === 'amber' ? 'text-amber-200' : 'text-blue-200';
  const buttonClass = variant === 'amber' ? 'bg-amber-800 hover:bg-amber-900' : 'bg-blue-800 hover:bg-blue-900';

  return (
    <div 
      className={`${bgClass} text-white px-6 py-3 flex items-center justify-between cursor-pointer transition`}
      onClick={onToggle}
    >
      <div className="flex items-center gap-2">
        {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        <MapPin className="w-5 h-5" />
        <h3 className="font-semibold text-lg">{location.name}</h3>
        <span className={`text-sm ${textClass}`}>
          ({beerCount} beer{beerCount !== 1 ? 's' : ''})
        </span>
      </div>
      {onViewTapList && (
        <button
          onClick={(e) => { 
            e.stopPropagation(); 
            onViewTapList(location);
          }}
          className={`${buttonClass} text-white px-3 py-1 rounded text-sm`}
        >
          View Tap List
        </button>
      )}
    </div>
  );
};

/**
 * SublocationGroup - Group of beers within a sublocation
 */
const SublocationGroup = ({ 
  group, 
  location,
  account, 
  calculateAverageRating, 
  getTotalRatings, 
  renderStars,
  onOpenRatingModal, 
  onViewKeg,
  variant = 'amber'
}) => {
  const accentColor = variant === 'amber' ? 'text-amber-600' : 'text-blue-600';
  const borderColor = variant === 'amber' ? 'border-amber-500' : 'border-blue-500';

  // Sort beers by tap order
  const sortedBeers = sortBeersByTapOrder(group.beers, [location], location.name, group.sublocation);

  return (
    <div>
      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
        <span className={accentColor}>→</span>
        {group.sublocation}
      </h4>
      <div className="space-y-4">
        {sortedBeers.map(keg => (
          <BeerCard
            key={keg.id}
            keg={keg}
            account={account}
            calculateAverageRating={calculateAverageRating}
            getTotalRatings={getTotalRatings}
            renderStars={renderStars}
            onOpenRatingModal={onOpenRatingModal}
            onViewKeg={onViewKeg}
            borderColor={borderColor}
            showTapNumber={variant === 'amber'}
            showStatus={variant === 'blue'}
            location={location}
            sublocationName={group.sublocation}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * DashboardBeersTab - Main beers on tap tab component
 */
export const DashboardBeersTab = ({ 
  locations,
  beersByLocation, 
  expandedLocations, 
  toggleLocationExpansion,
  account,
  calculateAverageRating,
  getTotalRatings,
  renderStars,
  onOpenRatingModal, 
  onViewKeg 
}) => {
  return (
    <div className="space-y-4">
      {locations.map(location => {
        const locationBeers = beersByLocation.filter(g => g.locationId === location.id);
        if (locationBeers.length === 0) return null;

        const isExpanded = expandedLocations[location.id] !== undefined 
          ? expandedLocations[location.id] 
          : true;

        const totalBeerCount = locationBeers.reduce((acc, g) => acc + g.beers.length, 0);

        const handleViewTapList = (loc) => {
          const url = locationBeers.length === 1 && locationBeers[0].sublocation
            ? `/taps/${encodeURIComponent(loc.name)}/${encodeURIComponent(locationBeers[0].sublocation)}`
            : `/taps/${encodeURIComponent(loc.name)}`;
          window.open(url, '_blank');
        };

        return (
          <div key={location.id} className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <LocationHeader
              location={location}
              beerCount={totalBeerCount}
              isExpanded={isExpanded}
              onToggle={() => toggleLocationExpansion(location.id)}
              onViewTapList={handleViewTapList}
              variant="amber"
            />
            
            {isExpanded && (
              <div className="p-6 space-y-6">
                {locationBeers.map(group => (
                  <SublocationGroup
                    key={group.sublocationId}
                    group={group}
                    location={location}
                    account={account}
                    calculateAverageRating={calculateAverageRating}
                    getTotalRatings={getTotalRatings}
                    renderStars={renderStars}
                    onOpenRatingModal={onOpenRatingModal}
                    onViewKeg={onViewKeg}
                    variant="amber"
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DashboardBeersTab;
